import clsx from 'clsx';
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  type Variants,
} from 'framer-motion';
import { forwardRef, useContext, useState } from 'react';

import AnimationsJS from '@dynatrace/strato-design-tokens/animations-js';

import {
  accordionContentCSS,
  accordionContentWrapperCSS,
} from './Accordion.sty.js';
import { AccordionSectionContext } from './contexts/AccordionSectionContext.js';
import { DataTestId } from '../../core/types/data-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { canReceiveDefaultTextFormat as _canReceiveDefaultTextFormat } from '../../core/utils/can-receive-default-text-format.js';
import { expandCollapseEasing } from '../../core/utils/framer-motion-easings.js';
import { Text } from '../../typography/text/Text.js';


/** @public */
export type AccordionSectionContentProps = WithChildren &
  StylingProps &
  DataTestId;

//variant for accordion section expand and collapse animation
const animationVariantSectionContent: Variants = {
  expanded: {
    height: 'auto',
    opacity: 1,
  },
  collapsed: {
    height: 0,
    opacity: 0,
  },
};
// #endregion

/**
 * Accordion Section Content component
 * @public
 */
export const AccordionSectionContent = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  AccordionSectionContentProps
>((props, forwardedRef) => {
  const {
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
  }: AccordionSectionContentProps = props;
  const { labelId, contentId, isExpanded, keepMounted, size } = useContext(
    AccordionSectionContext,
  );
  const childIsText = _canReceiveDefaultTextFormat(children);
  const [isAnimating, setIsAnimating] = useState(false);
  const shouldReduceMotion = useReducedMotion();

  const content = (
    <motion.div
      data-testid={dataTestId}
      variants={animationVariantSectionContent}
      initial="collapsed"
      animate={isExpanded ? 'expanded' : 'collapsed'}
      exit="collapsed"
      transition={{
        duration: shouldReduceMotion
          ? 0
          : AnimationsJS.ExpandCollapse.Duration.Base,
        ease: expandCollapseEasing,
      }}
      className={clsx(accordionContentWrapperCSS, consumerClassName)}
      style={{
        ...consumerStyle,
        willChange: 'initial',
        overflow:
          keepMounted && (isAnimating || !isExpanded) ? 'hidden' : undefined,
      }}
      onAnimationStart={() => {
        setIsAnimating(true);
      }}
      onAnimationComplete={() => {
        setIsAnimating(false);
      }}
    >
      <div
        className={clsx([
          accordionContentCSS({
            size,
          }),
          consumerClassName,
        ])}
        ref={forwardedRef}
        id={contentId}
        role="region"
        aria-labelledby={labelId}
      >
        {childIsText ? (
          <Text textStyle="base" as="p">
            {children}
          </Text>
        ) : (
          children
        )}
      </div>
    </motion.div>
  );

  if (keepMounted) {
    return content;
  }

  return (
    <div style={{ overflow: isAnimating ? 'hidden' : undefined }}>
      <AnimatePresence initial={false}>{isExpanded && content}</AnimatePresence>
    </div>
  );
});

(
  AccordionSectionContent as typeof AccordionSectionContent & {
    displayName: string;
  }
).displayName = 'Accordion.SectionContent';
